import { Link } from 'react-router-dom';

import { StateMessage } from '@/components/ui/controls';
import { Badge, Card } from '@/components/ui/page';
import { btn } from '@/components/ui/styles';
import { useApi } from '@/utils/use-api';

import type { ChannelSettings } from './api-types';
import SettingsHeader from './settings-nav';

type Channel = ChannelSettings['channels'][number];

const TITLES: Record<Channel['key'], string> = { email: 'Email', telegram: 'Telegram bot', popup: 'Owner Hub notifications' };

const STATUS: Record<Channel['status'], { label: string; tone: 'ok' | 'info' | 'neutral' | 'warn' }> = {
  ready: { label: 'Ready', tone: 'ok' },
  simulated: { label: 'Simulated', tone: 'info' },
  off: { label: 'Off', tone: 'neutral' },
  not_set_up: { label: 'Not set up', tone: 'warn' },
  waiting: { label: 'Waiting', tone: 'warn' },
};

/**
 * Outbound channels (OUT-1, OUT-4…6): email, the Telegram bot and the notification in the
 * casino owner dashboard, and whether messages can go out on each (GET /settings/channels).
 */
const ChannelsSettingsPage = () => {
  const { data, error } = useApi<ChannelSettings>('/settings/channels');

  return (
    <div className="max-w-4xl">
      <SettingsHeader />
      {!data ? (
        error ? (
          <StateMessage title="The channels could not be loaded" tone="error">
            {error}
          </StateMessage>
        ) : (
          <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
        )
      ) : (
        <div className="space-y-4">
          {data.channels.map((c) => {
            const status = STATUS[c.status] ?? { label: c.status, tone: 'neutral' as const };
            return (
              <Card
                key={c.key}
                notYet={c.key === 'email' ? 'email' : undefined}
                title={
                  <span className="flex flex-wrap items-center gap-2">
                    {TITLES[c.key] ?? c.key}
                    <Badge tone={status.tone}>{c.question ? `${status.label} on ${c.question}` : status.label}</Badge>
                  </span>
                }
              >
                <p className="text-sm text-fg">{c.detail}</p>
                {c.key === 'email' && <p className="mt-1 text-xs text-fg-faint">Every lead gets updates by email from the first contact on (OUT-5).</p>}
                {c.key === 'telegram' && (
                  <Link to="/settings/telegram" className={`${btn.link} mt-1 inline-flex min-h-11 items-center md:mt-2 md:inline-block md:min-h-0`}>
                    Telegram bot settings
                  </Link>
                )}
                {c.question && <p className="mt-1 text-xs text-fg-faint">It waits on open question {c.question} (docs/open-questions.md).</p>}
              </Card>
            );
          })}
          <p className="text-xs text-fg-faint">The same message may go out on several channels; nobody gets it deduplicated across them (OUT-4).</p>
        </div>
      )}
    </div>
  );
};

export default ChannelsSettingsPage;
